
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/lib/toast";
import { ExternalLink, Instagram, CheckCircle, AlertCircle, Copy, TestTube } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const InstagramConfig = () => {
  const [config, setConfig] = useState({
    clientId: "1091096916228788",
    clientSecret: "da4f069360d425aa3cc668162a3d9716",
    webhookUrl: "https://hajguvculrsuylksuylq.supabase.co/functions/v1/facebook-webhook",
    verifyToken: "minhagencia_webhook_2024_secure_token",
    callbackUrl: "https://minhagencia.digital/auth/facebook/callback"
  });
  
  const [isConnected, setIsConnected] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);

  useEffect(() => {
    const savedToken = localStorage.getItem("instagram_access_token");
    if (savedToken) {
      setAccessToken(savedToken);
      setIsConnected(true);
    }
  }, []);

  const handleConnect = () => {
    const authUrl = new URL("https://www.facebook.com/v18.0/dialog/oauth");
    authUrl.searchParams.append("client_id", config.clientId);
    authUrl.searchParams.append("redirect_uri", config.callbackUrl);
    authUrl.searchParams.append("scope", "instagram_basic,instagram_content_publish,pages_show_list,pages_read_engagement");
    authUrl.searchParams.append("response_type", "code");
    authUrl.searchParams.append("state", "instagram_auth");
    
    window.location.href = authUrl.toString();
  };

  const handleDisconnect = () => {
    localStorage.removeItem("instagram_access_token");
    localStorage.removeItem("instagram_token_expires");
    setAccessToken(null);
    setIsConnected(false);
    toast.success("Instagram desconectado com sucesso!");
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado!`);
  };

  const handleTestWebhook = async () => {
    try {
      const testUrl = `${config.webhookUrl}?hub.mode=subscribe&hub.verify_token=${config.verifyToken}&hub.challenge=test123`;
      console.log('Testing webhook URL:', testUrl);
      
      const response = await fetch(testUrl, {
        method: "GET"
      });
      
      console.log('Webhook test response:', response.status, response.statusText);
      
      if (response.ok) {
        const result = await response.text();
        console.log('Webhook test result:', result);
        
        if (result === "test123") {
          toast.success("✅ Webhook está funcionando corretamente!");
        } else if (result.includes("Facebook Webhook Endpoint")) {
          toast.success("✅ Endpoint está ativo e pronto!");
        } else {
          toast.warning(`Resposta inesperada: ${result}`);
        }
      } else {
        toast.error(`❌ Erro ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Webhook test error:', error);
      toast.error("❌ Erro ao conectar com o webhook");
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Instagram className="h-8 w-8 text-pink-600" />
        <div>
          <h1 className="text-2xl font-bold">Configuração do Instagram</h1>
          <p className="text-muted-foreground">Configure sua integração com o Instagram Business</p>
        </div>
      </div>

      {/* Status da Conexão */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Status da Conexão
            {isConnected ? (
              <CheckCircle className="h-5 w-5 text-green-500" />
            ) : (
              <AlertCircle className="h-5 w-5 text-orange-500" />
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <Badge variant={isConnected ? "default" : "secondary"}>
                {isConnected ? "Conectado" : "Desconectado"}
              </Badge>
              {accessToken && (
                <p className="text-sm text-muted-foreground mt-2">
                  Token: {accessToken.substring(0, 20)}...
                </p>
              )}
            </div>
            <div className="flex gap-2">
              {isConnected ? (
                <Button onClick={handleDisconnect} variant="outline">
                  Desconectar
                </Button>
              ) : (
                <Button onClick={handleConnect} className="flex items-center gap-2">
                  <Instagram className="h-4 w-4" />
                  Conectar Instagram
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Passo a Passo CRÍTICO */}
      <Card className="border-red-200 bg-red-50">
        <CardHeader>
          <CardTitle className="text-red-800">🚨 Configuração OBRIGATÓRIA no Facebook</CardTitle>
          <CardDescription className="text-red-600">
            Siga EXATAMENTE estes passos para funcionar
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-white p-4 rounded-lg border space-y-3">
            <h4 className="font-semibold text-red-800">1. Configure as URLs de Redirecionamento</h4>
            <p className="text-sm text-red-600">
              No Facebook Developer Console, vá em <strong>Configurações do Aplicativo {">"} Básico</strong>
            </p>
            
            <div className="space-y-2">
              <div>
                <Label className="text-sm font-medium">URLs de Redirecionamento OAuth Válidas:</Label>
                <div className="flex gap-2 items-center">
                  <Input
                    value={config.callbackUrl}
                    readOnly
                    className="bg-gray-50"
                  />
                  <Button 
                    onClick={() => copyToClipboard(config.callbackUrl, "URL de Callback")}
                    variant="outline" 
                    size="sm"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              <div>
                <Label className="text-sm font-medium">Domínios de Aplicativo:</Label>
                <div className="flex gap-2 items-center">
                  <Input
                    value="minhagencia.digital"
                    readOnly
                    className="bg-gray-50"
                  />
                  <Button 
                    onClick={() => copyToClipboard("minhagencia.digital", "Domínio")}
                    variant="outline" 
                    size="sm"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border space-y-3">
            <h4 className="font-semibold text-red-800">2. Configure o Webhook</h4>
            <p className="text-sm text-red-600">
              Vá em <strong>Produtos {">"} Webhooks {">"} Instagram</strong>
            </p>
            
            <div className="space-y-2">
              <div>
                <Label className="text-sm font-medium">URL de Callback:</Label>
                <div className="flex gap-2 items-center">
                  <Input
                    value={config.webhookUrl}
                    readOnly
                    className="bg-gray-50"
                  />
                  <Button 
                    onClick={() => copyToClipboard(config.webhookUrl, "URL do Webhook")}
                    variant="outline" 
                    size="sm"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              <div>
                <Label className="text-sm font-medium">Token de Verificação:</Label>
                <div className="flex gap-2 items-center">
                  <Input
                    value={config.verifyToken}
                    readOnly
                    className="bg-gray-50"
                  />
                  <Button 
                    onClick={() => copyToClipboard(config.verifyToken, "Token de Verificação")}
                    variant="outline" 
                    size="sm"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-2 flex-wrap">
            <Button onClick={handleTestWebhook} variant="outline" className="flex items-center gap-2">
              <TestTube className="h-4 w-4" />
              Testar Webhook
            </Button>
            <Button asChild variant="outline">
              <a 
                href="https://developers.facebook.com/apps/1091096916228788/settings/basic/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2"
              >
                <ExternalLink className="h-4 w-4" />
                Configurações Básicas
              </a>
            </Button>
            <Button asChild variant="outline">
              <a 
                href="https://developers.facebook.com/apps/1091096916228788/webhooks/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2"
              >
                <ExternalLink className="h-4 w-4" />
                Configurar Webhooks
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Configurações do App */}
      <Card>
        <CardHeader>
          <CardTitle>Informações do App</CardTitle>
          <CardDescription>
            Dados do seu Facebook App
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="clientId">App ID</Label>
              <div className="flex gap-2">
                <Input
                  id="clientId"
                  value={config.clientId}
                  readOnly
                  className="bg-gray-50"
                />
                <Button 
                  onClick={() => copyToClipboard(config.clientId, "App ID")}
                  variant="outline" 
                  size="sm"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div>
              <Label htmlFor="clientSecret">App Secret</Label>
              <div className="flex gap-2">
                <Input
                  id="clientSecret"
                  type="password"
                  value={config.clientSecret}
                  readOnly
                  className="bg-gray-50"
                />
                <Button 
                  onClick={() => copyToClipboard(config.clientSecret, "App Secret")}
                  variant="outline" 
                  size="sm"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Verificação */}
      <Card>
        <CardHeader>
          <CardTitle>✅ Lista de Verificação</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <input type="checkbox" id="check1" />
              <label htmlFor="check1" className="text-sm">
                Adicionei <code>https://minhagencia.digital/auth/facebook/callback</code> nas URLs de redirecionamento OAuth
              </label>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="check2" />
              <label htmlFor="check2" className="text-sm">
                Adicionei <code>minhagencia.digital</code> nos domínios do aplicativo
              </label>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="check3" />
              <label htmlFor="check3" className="text-sm">
                Configurei o webhook com a URL e token corretos
              </label>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="check4" />
              <label htmlFor="check4" className="text-sm">
                Testei o webhook e obtive resposta de sucesso
              </label>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="check5" />
              <label htmlFor="check5" className="text-sm">
                App está em modo de desenvolvimento ou live
              </label>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default InstagramConfig;
